// ─── Cible d'une discussion : grammaire PURE (Core) ───────────────
// Une discussion se rattache soit à l'ESPACE lui-même, soit à un ÉLÉMENT de cet
// espace (calendrier, inventaire, plan…). Décidé par Cédric le 09/08 :
// « attachement possible sur un espaceId ou un élément de l'espaceId ».
//
// On ne crée AUCUN vocabulaire : la clé est celle que `services/elements.js`
// compose déjà pour les jetons — `"<type>:<id>"` (`calendrier:abc`). Réutiliser
// évite deux grammaires concurrentes pour désigner la même chose.
//
// Module PUR (aucun import Firestore) : c'est ce qui le rend testable, et il
// porte les décisions qu'on ne veut pas voir se disperser dans les écrans.

// Types d'éléments qui peuvent porter une discussion. `espace` est le conteneur
// lui-même ; les autres sont les éléments déjà connus du système de jetons.
export const TYPES_CIBLE = ['espace', 'calendrier', 'inventaire', 'plan'];

// « calendrier:abc » ← ('calendrier', 'abc')
export const cleCible = (type, id) => (type && id ? `${type}:${id}` : null);

// « calendrier:abc » → { type:'calendrier', id:'abc' }. Renvoie null si la clé
// est vide, mal formée, ou porte un type inconnu — un appelant qui reçoit null
// doit refuser l'opération plutôt que deviner.
export function lireCible(cle) {
  if (typeof cle !== 'string') return null;
  const i = cle.indexOf(':');
  if (i <= 0 || i === cle.length - 1) return null;
  const type = cle.slice(0, i);
  const id = cle.slice(i + 1);
  if (!TYPES_CIBLE.includes(type)) return null;
  return { type, id };
}

// Id DÉTERMINISTE de la Générale d'une cible. Déterministe = deux appareils qui
// la créent en même temps écrivent le MÊME document, donc pas de doublon.
// Forme héritée de MoPCal (`gen_<calId>`) : conservée telle quelle pour que les
// discussions existantes — dont `gen_ivv` — restent à leur place.
export const idGenerale = (cle) => {
  const c = lireCible(cle);
  return c ? `gen_${c.id}` : null;
};

// La Générale d'un CALENDRIER existe toujours (règle produit, Cédric 09/08 :
// « pour partager sur le calendrier et ses membres »). Ailleurs, une discussion
// générale n'a pas de raison d'être imposée.
export const generaleObligatoire = (cle) => lireCible(cle)?.type === 'calendrier';

// Modèle d'une Générale — sert à la créer ET à l'afficher avant sa création.
// Champs de cible à ÉCRIRE sur un document. `cible` sert aux requêtes ; le
// type et l'id sont stockés DÉCOMPOSÉS parce que les règles Firestore ne
// savent pas redécouper une clé dont l'id contiendrait un « : ». Leur
// cohérence est revérifiée par la règle de création.
export function champsCible(cle) {
  const c = lireCible(cle);
  return c ? { cible: cle, cibleType: c.type, cibleId: c.id } : null;
}

export function modeleGenerale(cle, { espaceId, titre } = {}) {
  const c = lireCible(cle);
  if (!c) return null;
  return {
    id: idGenerale(cle),
    espaceId: espaceId || null,
    ...champsCible(cle),
    titre: titre || 'Général',
    generale: true,
    visibleATous: true,
    participants: [],
    ferme: false,
  };
}

// ─── Cloisonnement : l'unité est la DISCUSSION ────────────────────
// Corrigé par Cédric le 09/08 : « on ne cloisonne pas les messages… mais la
// discussion ». Les messages n'ont pas de droits propres, ils HÉRITENT du leur.
// Une discussion restreinte n'est donc pas seulement muette pour les autres :
// elle est INVISIBLE — son titre ne sort pas de la base.
//
// Le piège Firestore (une requête LISTE échoue ENTIÈREMENT si un seul document
// matché est illisible) se contourne en ne matchant JAMAIS que du lisible —
// deux requêtes par cible, dont l'union est exactement ce qu'on doit voir :
//   1) cible == <cle> ET visibleATous == true
//   2) cible == <cle> ET lecteurs array-contains <uid>
// Une discussion restreinte dont on n'est pas membre n'est jamais matchée, donc
// jamais refusée. Voir `lecteursDe` juste en dessous.

// Index de REQUÊTE de la sélection — pas une autorisation.
// Vide si `visibleATous` : c'est la requête 1 qui la ramène, et une liste de
// membres matérialisée finirait par diverger à chaque arrivée dans l'espace.
export const lecteursDe = (disc) =>
  (disc?.visibleATous ? [] : participantsEffectifs(disc || {}));

// Droit de LIRE = intersection des trois étages, évaluée à la lecture.
// `voitCible` est fourni par l'appelant (droit vivant sur le calendrier ou
// l'espace) ; les règles Firestore refont le même calcul de leur côté.
//
// Pourquoi une intersection VIVANTE plutôt qu'un instantané : retirer quelqu'un
// d'un étage lui coupe l'accès aux étages du dessous sans qu'aucune liste ne
// soit à nettoyer — et le RÉINTÉGRER lui rend tout, puisque sa sélection n'a
// jamais été effacée (demande de Cédric : « aucun impact »).
export function peutVoirDiscussion(disc, uid, voitCible) {
  if (!disc || !uid || !voitCible) return false;
  if (disc.generale || disc.visibleATous) return true;
  if (disc.creePar === uid) return true;
  return (disc.participants || []).includes(uid);
}

// Droit d'ADMINISTRER (renommer, fermer, gérer la sélection, supprimer).
// Volontairement DISJOINT de la lecture : le propriétaire de l'espace hérite des
// droits des partants pour pouvoir faire le ménage, mais n'accède JAMAIS au
// contenu d'une discussion où il n'a pas été sélectionné (arbitrage de Cédric,
// 09/08 — « le créateur de la discussion n'est pas le créateur du calendrier »).
// `createurPresent` : le créateur est-il encore membre ? Fourni par l'appelant.
export function peutAdministrerDiscussion(disc, uid, { proprietaireEspace, createurPresent } = {}) {
  if (!disc || !uid) return false;
  if (disc.generale) return false;             // la Générale ne se ferme ni ne se supprime
  if (disc.creePar === uid) return true;
  return !createurPresent && uid === proprietaireEspace;
}

// Compat de lecture : ancienne signature sans le droit sur la cible. Réservée
// aux listes DÉJÀ restreintes à une cible visible (l'écran ne requête que des
// cibles qu'il voit) — ne pas l'utiliser pour décider d'un accès.
export const discussionVisible = (disc, uid) => peutVoirDiscussion(disc, uid, true);

// Discussions d'une cible, visibles par `uid`, Générale en tête puis les plus
// récentes d'abord. Les discussions FERMÉES restent listées (consultables) mais
// passent après les ouvertes : elles sont de l'archive, pas du courant.
export function trierDiscussions(liste, uid) {
  return (liste || [])
    .filter(d => discussionVisible(d, uid))
    .sort((a, b) => {
      if (a.generale !== b.generale) return a.generale ? -1 : 1;
      if (!!a.ferme !== !!b.ferme) return a.ferme ? 1 : -1;
      return (b.creeLe || '').localeCompare(a.creeLe || '');
    });
}

// Participants effectifs d'une discussion restreinte : le créateur en fait
// TOUJOURS partie, sinon il perdrait l'accès à ce qu'il vient de créer.
export function participantsEffectifs({ visibleATous, participants, creePar }) {
  if (visibleATous) return [];
  return Array.from(new Set([...(participants || []), creePar].filter(Boolean)));
}

// ─── Fermeture : SANS RETOUR ──────────────────────────────────────
// Cédric, 09/08 : « on ne rouvre pas une discussion, on en crée une nouvelle si
// besoin ». MoPCal permettait de rebasculer `ferme` ; on ne le reprend pas.
// Une discussion fermée reste LISIBLE (c'est de l'archive), simplement close.
// Écrit ici pour que la règle Firestore et l'écran appliquent la MÊME chose.
export const CHAMPS_ADMINISTRABLES = [
  'titre', 'participants', 'lecteurs', 'visibleATous', 'ferme', 'fermeeLe', 'fermeePar',
];

export function transitionValide(avant, apres) {
  if (!avant || !apres) return false;
  if (avant.ferme === true && apres.ferme !== true) return false;   // pas de réouverture
  const touches = Object.keys(apres).filter(k => apres[k] !== avant[k]);
  return touches.every(k => CHAMPS_ADMINISTRABLES.includes(k));
}

// ─── Qui peut CRÉER, et sur quelle cible ──────────────────────────
// Sur l'ESPACE : son propriétaire seul. Sur un ÉLÉMENT (calendrier…) : tous
// ceux qui le voient. `voitCible` est le droit vivant, fourni par l'appelant.
export function peutCreerDiscussion(cle, uid, { proprietaireEspace, voitCible } = {}) {
  const c = lireCible(cle);
  if (!c || !uid) return false;
  if (c.type === 'espace') return uid === proprietaireEspace;
  return !!voitCible;
}

// Cibles qu'une APP propose à la création. Le Core sait rattacher une discussion
// à l'espace ; **My Calendar ne l'expose pas** (Cédric, 09/08 : « dans My
// Calendar on ne peut pas créer de discussion sur l'espaceId, cette information
// était pour la structure dans le Core »). La capacité reste dans le modèle pour
// les autres apps — on ne la retire pas, on ne l'offre simplement pas ici.
export const CIBLES_PAR_APP = {
  'mop-my-calendar': ['calendrier'],
};

export function ciblesCreables(appId, typesDisponibles = TYPES_CIBLE) {
  const permis = CIBLES_PAR_APP[appId];
  return permis ? typesDisponibles.filter(t => permis.includes(t)) : typesDisponibles;
}
